import { LANGUAGES, TONES } from '@lyrmo/shared/options';
import { Fragment, useRef, type CSSProperties, type PointerEvent, type ReactNode } from 'react';
import { BrandMark } from '../../components/BrandMark';
import { Icon, type IconName } from '../../components/Icon';
import { useCountingNumber } from '../../hooks/useCountingNumber';
import { useI18n } from '../../i18n/i18n-context';
import { AppLink } from '../../layout/AppLink';
import { DesktopPromo } from '../downloads/DesktopPromo';
import { DESKTOP_OFFERED, chromeExtension, desktopPlatforms } from '../downloads/systems';
import { HomeFilm, type HomeFilmHandle } from './HomeFilm';
import { HomeFeature } from './HomeFeature';
import { FEATURE_PROMOS, OVERVIEW, clock } from '../promo/promos';
import './home.css';

// Every language a rewrite can be written in; "auto" only keeps the text's own.
const WRITING_LANGUAGES = LANGUAGES.filter((language) => language !== 'auto').length;

/**
 * The title as words that come into focus one after another (--word), the accent's in the brand's
 * colour. Spaces stay between them, so lines wrap as they would anyway.
 */
function titleWords(template: string, accent: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let word = 0;
  for (const [index, part] of template.split(/(\{accent\})/).entries()) {
    const isAccent = part === '{accent}';
    const spans: ReactNode[] = [];
    for (const [at, piece] of (isAccent ? accent : part).split(/(\s+)/).entries()) {
      spans.push(
        /^\s*$/.test(piece) ? (
          piece
        ) : (
          <span key={at} className="home-word" style={{ '--word': word++ } as CSSProperties}>
            {piece}
          </span>
        ),
      );
    }
    // Only the accent is wrapped, so the spaces around it stay part of the heading's name.
    nodes.push(
      isAccent ? (
        <span key={index} className="home-title-accent">
          {spans}
        </span>
      ) : (
        <Fragment key={index}>{spans}</Fragment>
      ),
    );
  }
  return nodes;
}

/** A number in the hero that counts up to its value as the page opens. */
function Stat({ value, label }: { readonly value: number; readonly label: string }) {
  const { format } = useI18n();
  const shown = useCountingNumber(value);
  return (
    <li className="home-stat">
      <span className="home-stat-value" aria-hidden="true">
        {format.number(shown)}
      </span>
      <span className="home-stat-label">
        <span className="sr-only">{format.number(value)} </span>
        {label}
      </span>
    </li>
  );
}

/** Points the way the page reads: right, or left in a right-to-left language. */
function Arrow() {
  return (
    <span className="home-arrow" aria-hidden="true">
      <Icon name="arrow" />
    </span>
  );
}

/** Lights the part of a card under the pointer (--spot-x and -y, home.css). */
function followPointer(event: PointerEvent<HTMLElement>) {
  if (event.pointerType !== 'mouse') return;
  const card = (event.target as Element).closest<HTMLElement>('.home-feature');
  if (!card) return;
  const box = card.getBoundingClientRect();
  card.style.setProperty('--spot-x', `${String(event.clientX - box.left)}px`);
  card.style.setProperty('--spot-y', `${String(event.clientY - box.top)}px`);
}

const VALUES: readonly {
  readonly id: 'author' | 'free';
  readonly icon: IconName;
}[] = [
  { id: 'author', icon: 'edit' },
  { id: 'free', icon: 'heart' },
];

/** What Lyrmo is and does, told around the overview film: the site's home page. */
export function HomePage() {
  const { t } = useI18n();
  const page = useRef<HTMLDivElement>(null);
  const film = useRef<HomeFilmHandle>(null);
  const { home } = t;

  return (
    <div
      className="home-page"
      ref={page}
      onPlayCapture={(event) => {
        // Keep the overview, demos and desktop film from speaking over one another.
        if (!(event.target instanceof HTMLVideoElement)) return;
        for (const video of page.current?.querySelectorAll('video') ?? []) {
          if (video !== event.target && !video.paused) video.pause();
        }
      }}
    >
      <section className="home-hero" aria-labelledby="home-heading">
        <div className="home-hero-backdrop" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <p className="home-eyebrow">
          <span className="home-eyebrow-mark" aria-hidden="true">
            <BrandMark />
          </span>
          {t.app.badge}
        </p>
        <h1 className="home-title" id="home-heading">
          {titleWords(home.title, home.titleAccent)}
        </h1>
        <p className="home-lede">{home.description}</p>
        <div className="home-actions">
          <AppLink className="button button--primary home-action" to="write">
            {home.start}
            <Arrow />
          </AppLink>
          <button
            type="button"
            className="button home-action home-action--quiet"
            onClick={() => {
              film.current?.watch();
            }}
          >
            <Icon name="play" />
            {home.watch}
            <span className="home-action-meta">{clock(OVERVIEW.duration)}</span>
          </button>
        </div>
        <ul className="home-stats">
          <Stat value={4} label={home.stats.versions(4)} />
          <Stat value={TONES.length} label={home.stats.tones(TONES.length)} />
          <Stat value={WRITING_LANGUAGES} label={home.stats.languages(WRITING_LANGUAGES)} />
        </ul>
      </section>

      <HomeFilm ref={film} />

      <section className="home-section" aria-labelledby="home-features-heading">
        <div className="home-section-head home-reveal">
          <h2 id="home-features-heading">{home.featuresTitle}</h2>
          <p>{home.featuresDescription}</p>
        </div>
        <div className="home-feature-grid" onPointerMove={followPointer}>
          {FEATURE_PROMOS.map((promo, index) => (
            <HomeFeature key={promo.id} promo={promo} index={index} />
          ))}
        </div>
      </section>

      {DESKTOP_OFFERED && (
        <section className="home-section home-desktop" aria-labelledby="home-desktop-heading">
          <div className="home-desktop-text home-reveal">
            <p className="home-kicker">{t.navigation.desktop}</p>
            <h2 id="home-desktop-heading">{home.desktopTitle}</h2>
            <p className="home-desktop-description">{home.desktopDescription}</p>
            <ul className="home-systems">
              {desktopPlatforms.map((platform) => (
                <li key={platform.id}>
                  <Icon name={platform.icon} />
                  {platform.name}
                </li>
              ))}
            </ul>
            <AppLink className="button button--primary home-action" to="desktop">
              {t.downloads.link}
              <Arrow />
            </AppLink>
          </div>
          <div className="home-desktop-film home-reveal">
            <DesktopPromo />
          </div>
        </section>
      )}

      <section className="home-section home-extension" aria-labelledby="home-extension-heading">
        <div className="home-section-head home-reveal">
          <p className="home-kicker">{t.navigation.extension}</p>
          <h2 id="home-extension-heading">{home.extensionTitle}</h2>
          <p>{home.extensionDescription}</p>
        </div>
        <div className="home-extension-action home-reveal">
          <a
            className="button button--primary home-action"
            href={chromeExtension.url}
            target="_blank"
            rel="noopener"
          >
            <Icon name={chromeExtension.icon} />
            {t.downloads.addToChrome}
            <Icon name="external" />
          </a>
        </div>
      </section>

      <section className="home-section" aria-labelledby="home-values-heading">
        <div className="home-section-head home-reveal">
          <h2 id="home-values-heading">{home.values.title}</h2>
        </div>
        <ul className="home-values">
          {VALUES.map(({ id, icon }) => (
            <li key={id} className="home-value home-reveal">
              <span className="home-value-icon" aria-hidden="true">
                <Icon name={icon} />
              </span>
              <h3>{home.values[`${id}Title`]}</h3>
              <p>{home.values[`${id}Body`]}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="home-closing home-reveal" aria-labelledby="home-closing-heading">
        <span className="home-closing-mark" aria-hidden="true">
          <BrandMark />
        </span>
        <h2 id="home-closing-heading">{home.closingTitle}</h2>
        <p>{home.closingDescription}</p>
        <div className="home-actions">
          <AppLink className="button button--primary home-action" to="write">
            {home.start}
            <Arrow />
          </AppLink>
          <AppLink className="button home-action home-action--quiet" to="guide">
            {t.navigation.guide}
          </AppLink>
        </div>
      </section>
    </div>
  );
}
